/**
 * Vertretungsplan (Jul-2026, User-Wunsch) — standortübergreifende Planung nach
 * Region: Standorte + Mitarbeiter mit Anfahrt von der Wohnadresse, Urlaub/
 * Krankheit und Wochen-Auslastung. Zahnrad = Plan-Einstellungen. KI-Panel
 * unterstützt aktiv mit Abdeckungs-Vorschlägen. Route: /dealer/vertretungsplan
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Settings, Sparkles, MapPin, Loader2, CalendarRange, Trash2, Plane, Thermometer } from "lucide-react";
import { useGearsVisible } from "../lib/gearVisibility";

const KIND_ICONS = { urlaub: <Plane className="w-3 h-3" />, krank: <Thermometer className="w-3 h-3" /> };

export default function VertretungsplanPage() {
    const gearsOk = useGearsVisible();

    const [data, setData] = useState(null);
    const [settings, setSettings] = useState(null);
    const [err, setErr] = useState("");
    const [gearOpen, setGearOpen] = useState(false);
    const [aiText, setAiText] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [assign, setAssign] = useState(null); // {employee} | null
    const [assignForm, setAssignForm] = useState({ site: "", from: "", to: "", note: "" });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [p, s] = await Promise.all([
                api.get("/dealer/vertretungsplan"),
                api.get("/dealer/vertretungsplan/settings"),
            ]);
            setData(p.data); setSettings(s.data); setErr("");
        } catch (e) { setErr(e.response?.data?.detail || "Kein Zugriff auf den Vertretungsplan."); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const saveSettings = async () => {
        setBusy(true);
        try {
            const { data: s } = await api.put("/dealer/vertretungsplan/settings", settings);
            setSettings(s); setGearOpen(false);
            toast.success("Plan-Einstellungen gespeichert");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        setBusy(false);
    };

    const runAi = async () => {
        setAiBusy(true); setAiText("");
        try {
            const { data: r } = await api.post("/dealer/vertretungsplan/ai-suggest", {});
            setAiText(r.suggestions || "Keine Vorschläge.");
        } catch (e) { toast.error(e.response?.data?.detail || "KI-Vorschläge fehlgeschlagen"); }
        setAiBusy(false);
    };

    const createAssignment = async () => {
        if (!assignForm.site || !assignForm.from) { toast.error("Bitte Standort und Startdatum wählen."); return; }
        setBusy(true);
        try {
            await api.post("/dealer/vertretungsplan/assignments", {
                employee_id: assign.id, site_dealer_id: assignForm.site,
                date_from: assignForm.from, date_to: assignForm.to || assignForm.from,
                note: assignForm.note,
            });
            toast.success(`Vertretung für ${assign.name} eingeplant — der Kollege bekommt einen Hinweis im Header.`);
            setAssign(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Einplanen fehlgeschlagen"); }
        setBusy(false);
    };

    const cancelAssignment = async (id) => {
        try { await api.delete(`/dealer/vertretungsplan/assignments/${id}`); toast.success("Vertretung storniert"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Stornieren fehlgeschlagen"); }
    };

    if (err) return <div className="max-w-3xl mx-auto p-8 text-red-700 font-bold" data-testid="vplan-error">{err}</div>;
    if (!data) return <div className="p-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;

    const bmap = Object.fromEntries(data.branches.map((b) => [b.id, b]));
    const siteLabel = (id) => { const b = bmap[id]; return b ? `${b.site_code || ""} ${b.city || b.name || ""}`.trim() : "?"; };

    return (
        <div className="max-w-7xl mx-auto px-4 py-8" data-testid="vertretungsplan-page">
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <h1 className="text-3xl font-display font-black">Vertretungsplan</h1>
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {data.date_from} – {data.date_to} · Region: {settings?.region_city || "alle Standorte"} · Umkreis {settings?.radius_km} km
                </span>
                <div className="ml-auto flex gap-2">
                    {gearsOk && <Button variant="outline" onClick={() => setGearOpen(true)} className="rounded-sm" data-testid="vplan-settings-btn">
                        <Settings className="w-4 h-4 mr-1" /> Einstellungen
                    </Button>}
                    <Button onClick={runAi} disabled={aiBusy} className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm" data-testid="vplan-ai-btn">
                        {aiBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />} KI-Vorschläge
                    </Button>
                </div>
            </div>

            {aiText && (
                <div className="mb-6 bg-yellow-50 border-l-4 border-[#FFCC00] rounded-sm p-4 whitespace-pre-wrap text-sm" data-testid="vplan-ai-output">
                    <div className="font-black uppercase text-xs tracking-wider mb-2 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> KI-Abdeckungs-Vorschläge</div>
                    {aiText}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white border border-gray-200 rounded-sm p-4">
                    <h2 className="font-display font-black text-lg mb-3">Mitarbeiter · Anfahrt & Verfügbarkeit</h2>
                    <div className="space-y-2">
                        {data.employees.map((e) => {
                            const trips = Object.entries(e.travel_km).sort((a, b) => a[1] - b[1]).slice(0, 4);
                            return (
                                <div key={e.id} className="border border-gray-100 rounded-sm p-3 flex flex-wrap items-center gap-3" data-testid={`vplan-emp-${e.id}`}>
                                    <div className="min-w-[180px]">
                                        <div className="font-bold text-sm">{e.name}</div>
                                        <div className="text-[11px] text-gray-500">{e.function || "Mitarbeiter"} · Stamm: {siteLabel(e.home_dealer_id)} · {e.week_hours} Std/Woche</div>
                                    </div>
                                    <div className="flex flex-wrap gap-1 flex-1">
                                        {e.absences.map((a, i) => (
                                            <span key={i} className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700">
                                                {KIND_ICONS[a.kind] || null} {a.kind} {a.date_from}{a.date_to !== a.date_from ? `–${a.date_to}` : ""}
                                            </span>
                                        ))}
                                        {!e.has_home_geo && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">Wohnadresse fehlt</span>}
                                        {trips.map(([bid, km]) => (
                                            <span key={bid} className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${km <= (settings?.max_travel_km || 50) ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-gray-50 border-gray-200 text-gray-400"}`}>
                                                <MapPin className="w-2.5 h-2.5" /> {siteLabel(bid)}: {km} km
                                            </span>
                                        ))}
                                    </div>
                                    <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs font-bold shrink-0"
                                        onClick={() => { setAssign(e); setAssignForm({ site: "", from: data.date_from, to: "", note: "" }); }}
                                        data-testid={`vplan-assign-${e.id}`}>
                                        <CalendarRange className="w-3.5 h-3.5 mr-1" /> Vertretung planen
                                    </Button>
                                </div>
                            );
                        })}
                        {!data.employees.length && <p className="text-sm text-gray-500">Keine Mitarbeiter gefunden.</p>}
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-sm p-4">
                    <h2 className="font-display font-black text-lg mb-3">Geplante Vertretungen</h2>
                    <div className="space-y-2" data-testid="vplan-assignments">
                        {data.assignments.map((a) => (
                            <div key={a.id} className="border border-gray-100 rounded-sm p-2.5 text-sm flex items-start gap-2">
                                <div className="flex-1">
                                    <div className="font-bold">{a.employee_name} → {a.site_label}</div>
                                    <div className="text-[11px] text-gray-500">{a.date_from} – {a.date_to}{a.note ? ` · ${a.note}` : ""}</div>
                                </div>
                                <button onClick={() => cancelAssignment(a.id)} className="text-gray-400 hover:text-red-600" title="Stornieren" data-testid={`vplan-cancel-${a.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                        {!data.assignments.length && <p className="text-sm text-gray-500">Noch keine Vertretungen geplant.</p>}
                    </div>
                    <div className="mt-4 text-[11px] text-gray-500 border-t pt-3">
                        Standorte im Plan: {data.branches.map((b) => b.site_code || b.city).filter(Boolean).join(", ")}
                    </div>
                </div>
            </div>

            {/* Zahnrad — Plan-Einstellungen */}
            <Dialog open={gearOpen} onOpenChange={setGearOpen}>
                <DialogContent className="max-w-md" data-testid="vplan-settings-dialog">
                    <DialogHeader><DialogTitle>Plan-Einstellungen</DialogTitle></DialogHeader>
                    {settings && (
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Region (Stadt, leer = alle Standorte)</label>
                                <Input value={settings.region_city} placeholder="z. B. Berlin"
                                    onChange={(e) => setSettings((s) => ({ ...s, region_city: e.target.value }))} data-testid="vplan-set-region" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Umkreis (km)</label>
                                    <Input type="number" min="1" value={settings.radius_km}
                                        onChange={(e) => setSettings((s) => ({ ...s, radius_km: e.target.value }))} data-testid="vplan-set-radius" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Max. Anfahrt (km)</label>
                                    <Input type="number" min="1" value={settings.max_travel_km}
                                        onChange={(e) => setSettings((s) => ({ ...s, max_travel_km: e.target.value }))} data-testid="vplan-set-travel" />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm font-semibold">
                                <Switch checked={!!settings.consider_absences}
                                    onCheckedChange={(v) => setSettings((s) => ({ ...s, consider_absences: v }))} data-testid="vplan-set-absences" />
                                Urlaub / Krankheit berücksichtigen
                            </label>
                            <div className="flex justify-end">
                                <Button onClick={saveSettings} disabled={busy} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="vplan-settings-save">
                                    {busy ? "Speichere…" : "Speichern"}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Vertretung einplanen */}
            <Dialog open={!!assign} onOpenChange={(v) => !v && setAssign(null)}>
                <DialogContent className="max-w-md" data-testid="vplan-assign-dialog">
                    <DialogHeader><DialogTitle>Vertretung planen — {assign?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Standort</label>
                            <Select value={assignForm.site} onValueChange={(v) => setAssignForm((f) => ({ ...f, site: v }))}>
                                <SelectTrigger data-testid="vplan-assign-site"><SelectValue placeholder="Standort wählen…" /></SelectTrigger>
                                <SelectContent>
                                    {data.branches.filter((b) => b.id !== assign?.home_dealer_id).map((b) => (
                                        <SelectItem key={b.id} value={b.id}>
                                            {b.name} {b.site_code ? `· ${b.site_code}` : ""} {b.city ? `· ${b.city}` : ""}
                                            {assign?.travel_km?.[b.id] != null ? ` — ${assign.travel_km[b.id]} km Anfahrt` : ""}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Von</label>
                                <Input type="date" value={assignForm.from} onChange={(e) => setAssignForm((f) => ({ ...f, from: e.target.value }))} data-testid="vplan-assign-from" />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-600">Bis</label>
                                <Input type="date" value={assignForm.to} onChange={(e) => setAssignForm((f) => ({ ...f, to: e.target.value }))} data-testid="vplan-assign-to" />
                            </div>
                        </div>
                        <Input placeholder="Notiz (optional)" value={assignForm.note} onChange={(e) => setAssignForm((f) => ({ ...f, note: e.target.value }))} data-testid="vplan-assign-note" />
                        <div className="flex justify-end">
                            <Button onClick={createAssignment} disabled={busy} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="vplan-assign-save">
                                {busy ? "Speichere…" : "Einplanen"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
